import { useQuery } from '@apollo/client';
import { Stack } from '@mui/material';
import { RuleGroupType } from '@procureai/query-builder';
import { Status, Widget, WidgetContent } from '@procureai/ui-mui';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { BaseDomain } from '~/common/types';
import { useConfig } from '~/components/ConfigProvider';
import { DataGridWidgetHeader, useDataGridStatsTranslation } from '~/components/DataGridCustom';
import { Map, MapMarkerVariant, MapProps } from '~/components/Map';
import { MapSourceId, MapViewState } from '~/components/Map/Map.types';
import { SupplierMapDetail } from '~/components/SupplierMapDetail/SupplierMapDetail';

import { CircularProgressPaperStyled, WidgetContentInner } from './CountryMapWidget.styled';
import { RISK_COUNTRY_QUERY } from './LocationGroup.gql';

const markerVariants: MapProps['markerVariants'] = {
  known: MapMarkerVariant.PRIMARY,
};

export interface MapWidgetProps {
  domain: BaseDomain;
  i18nPrefix: string;
  loading?: boolean;
  filter?: RuleGroupType;
  filterCount?: number;
  query: RuleGroupType | undefined;
}

interface RuleLocation {
  field: 'locationRadius';
  value: {
    lat: number;
    lng: number;
  };
}

export const CountryMapWidget = ({ filterCount, loading: loadingProp, query }: MapWidgetProps) => {
  const { t } = useTranslation();
  const { map, tenant } = useConfig();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [viewState, setViewState] = useState<MapViewState>({
    latitude: tenant.geo.latitude,
    longitude: tenant.geo.longitude,
    zoom: map.zoom,
  });

  useEffect(() => {
    const rules: RuleLocation[] = query
      ? query.rules.filter((rule): rule is RuleLocation => 'field' in rule && rule.field === 'locationRadius')
      : [];
    const location = rules.find((rule) => rule)?.value;

    if (location) {
      setViewState({
        latitude: location.lat,
        longitude: location.lng,
        zoom: map.zoom,
      });
    }
  }, [query, map]);

  const { loading: locationGroupLoading, data: locationGroupData } = useQuery(RISK_COUNTRY_QUERY, {
    fetchPolicy: 'cache-and-network',
  });

  const rowCount = locationGroupData?.cataclysm.countries.length || 0;
  const title = useDataGridStatsTranslation({ rowCount, filterCount });

  return (
    <Widget>
      <DataGridWidgetHeader
        title={title}
        center={
          <Stack
            direction="row"
            spacing={2}
            sx={{
              alignItems: 'center',
            }}
          >
            <Status statusColor="secondary.main" variant="caption">
              {t('supplierType.known')}
            </Status>
            <Status statusColor="text.primary" variant="caption">
              {t('supplierType.unknown')}
            </Status>
          </Stack>
        }
      />
      <WidgetContent disablePadding sx={{ minHeight: 400 }}>
        <WidgetContentInner>
          <Map
            viewState={viewState}
            mapStyle={map.style}
            markerVariants={markerVariants}
            sources={[
              {
                data: {
                  type: 'FeatureCollection',
                  features:
                    locationGroupData?.cataclysm.countries.map((p) => p.area as unknown as GeoJSON.Feature) ?? [],
                },
                id: MapSourceId.locationGroupData,
              },
            ]}
            loading={locationGroupLoading || loadingProp}
            onSelect={setSelectedId}
            onViewStateSelect={setViewState}
          />
          {locationGroupLoading && <CircularProgressPaperStyled elevation={9} label={t('Map.loading')} />}
          {selectedId ? (
            <SupplierMapDetail
              id={selectedId}
              onClose={() => {
                setSelectedId(null);
              }}
            />
          ) : null}
        </WidgetContentInner>
      </WidgetContent>
    </Widget>
  );
};
